import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, InputAdornment, LinearProgress, MenuItem, Stack, Switch, TextField, Typography,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { loadStripe } from '@stripe/stripe-js';
import { getFirestore, collection, query, where, getDocs, getDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import firebaseApp from '../../utils/firebaseConfig';
import { pickBestMember } from '../../utils/members';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';
const SESSION_KEY = 'reservation_user';

const formatPrice = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-PR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—');
const digits = (v) => String(v || '').replace(/\D/g, '');
const STATUS = {
  active: { label: 'Activo', color: 'info' },
  overdue: { label: 'Vencido', color: 'error' },
  paid: { label: 'Saldado · listo para recoger', color: 'success' },
  completed: { label: 'Entregado', color: 'default' },
  cancelled: { label: 'Cancelado', color: 'warning' },
};
const KIND = { deposit: 'Depósito inicial', payment: 'Abono', storage: 'Almacenaje', extension: 'Extensión de contrato', fee: 'Trámite fallecimiento / inscripción', note: 'Nota' };
const TAX = (c) => Math.round(c * 0.115);
// Meses de almacenaje vencidos (custodia o arma saldada sin recoger)
const owedMonths = (l, settings) => {
  if (!l) return 0;
  if (typeof l.monthsOwed === 'number') return l.monthsOwed;
  if (!(l.kind === 'custody' || l.status === 'paid') || l.daysLeft === null || l.daysLeft >= 0) return 0;
  return Math.ceil(-l.daysLeft / (settings.storageMonthDays || 30));
};

const CARD_EL_STYLE = {
  base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#999' } },
  invalid: { color: '#d40100' },
};

// Página pública: el cliente entra con su TELÉFONO + PIN (misma sesión que
// /make-reservation y /my-reservations), ve sus layaways de armas y abona la
// cantidad que quiera o paga el almacenaje mensual que extiende la fecha.
export default function MyLayawaysPage() {
  const [loginInput, setLoginInput] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [user, setUser] = useState(null); // { id?, fullName?, phone, email? }
  const [pinStep, setPinStep] = useState(null); // 'verify' | 'create' | null
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinVerified, setPinVerified] = useState(false);

  const [layaways, setLayaways] = useState([]);
  const [settings, setSettings] = useState({ storageFeeCents: 2000, storageFeeTaxable: true, storageMonthDays: 30, minPaymentCents: 500, customerCanPay: true, customerCanExtend: true, extensionFeeCents: 3000, extensionDays: 60, extensionFeeTaxable: true, extensionMaxCount: 1, storageMustPayOwed: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsOpen, setTermsOpen] = useState(false);

  // Pago
  const [payTarget, setPayTarget] = useState(null); // layaway
  const [payKind, setPayKind] = useState('payment');
  const [amount, setAmount] = useState('');
  const [months, setMonths] = useState(1);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [saveCard, setSaveCard] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState('');
  const [paySuccess, setPaySuccess] = useState(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);

  // Sesión guardada
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && (parsed.phone || parsed.email)) { setUser(parsed); setPinVerified(true); }
      }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchLayaways = useCallback(async () => {
    if (!user) return;
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/getMyLayaways`, { memberId: user.id || null, phone: user.phone || null, email: user.email || null });
      setLayaways(r.data.layaways || []);
      if (r.data.settings) setSettings((s) => ({ ...s, ...r.data.settings }));
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || 'No se pudieron cargar tus layaways');
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user && pinVerified) fetchLayaways(); }, [user, pinVerified, fetchLayaways]);

  const loadPaymentMethods = async (email) => {
    if (!email) return;
    try {
      const hap = await getDocs(query(collection(db, 'hap-members'), where('email', '==', email.toLowerCase())));
      const custId = hap.empty ? null : hap.docs[0].data().stripeCustomerId;
      if (!custId) return;
      setStripeCustomerId(custId);
      const r = await axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: custId });
      const pms = r.data.paymentMethods || [];
      setPaymentMethods(pms);
      if (pms.length) setSelectedPM(pms[0].id);
    } catch (e) { /* sin tarjetas guardadas */ }
  };

  // ----- Login por teléfono (o email) -----
  const handleLogin = async () => {
    const raw = loginInput.trim();
    if (!raw) { setLoginError('Escribe tu número de teléfono'); return; }
    setLoginLoading(true); setLoginError('');
    try {
      const isPhone = digits(raw).length >= 7 && !raw.includes('@');
      const phone = isPhone ? digits(raw) : '';
      const email = isPhone ? '' : raw.toLowerCase();
      let snap = await getDocs(query(collection(db, 'members'), where(isPhone ? 'phone' : 'email', '==', isPhone ? phone : email)));
      if (snap.empty && isPhone && phone.length === 10) {
        snap = await getDocs(query(collection(db, 'members'), where('phone', '==', `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`)));
      }
      let u;
      if (snap.empty) {
        // Puede tener layaway sin cuenta (el staff lo creó con el teléfono).
        const r = await axios.post(`${FUNCTIONS_BASE}/getMyLayaways`, { phone: phone || null, email: email || null });
        if (!(r.data.layaways || []).length) { setLoginError('No encontramos una cuenta ni layaways con ese dato. Verifica el número o pasa por la tienda.'); setLoginLoading(false); return; }
        u = { phone, email, fullName: r.data.layaways[0].memberName || '' };
      } else {
        const m = pickBestMember(snap.docs, { phone, email });
        u = { ...m, phone: m.phone ? digits(m.phone) : phone, email: (m.email || email || '').toLowerCase() };
      }
      setUser(u);
      const pinId = u.phone || u.email;
      try {
        const pinSnap = await getDoc(doc(db, 'user-pins', pinId));
        if (pinSnap.exists() || u.pin) setPinStep('verify'); else setPinStep('create');
      } catch (e) { setPinStep('create'); }
    } catch (e) {
      setLoginError('Error al verificar tu cuenta');
    }
    setLoginLoading(false);
  };

  const finishLogin = (u) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...u }));
    setPinVerified(true); setPinStep(null);
  };

  const handlePinVerify = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    setPinError('');
    try {
      const pinId = user.phone || user.email;
      const pinSnap = await getDoc(doc(db, 'user-pins', pinId));
      const ok = (pinSnap.exists() && pinSnap.data().pin === pinValue) || (user.pin && String(user.pin) === pinValue);
      if (ok) finishLogin(user); else { setPinError('PIN incorrecto. Intenta de nuevo.'); setPinValue(''); }
    } catch (e) { setPinError('Error al verificar el PIN'); }
  };

  const handlePinCreate = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    if (pinValue !== pinConfirm) { setPinError('Los PINs no coinciden'); setPinConfirm(''); return; }
    try {
      await setDoc(doc(db, 'user-pins', user.phone || user.email), { pin: pinValue, createdAt: serverTimestamp() });
      if (user.id) await setDoc(doc(db, 'members', user.id), { pin: pinValue }, { merge: true });
      finishLogin(user);
    } catch (e) { setPinError('Error al guardar el PIN'); }
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null); setPinVerified(false); setPinStep(null); setLayaways([]); setPinValue(''); setLoginInput('');
  };

  // ----- Pago -----
  const openPay = async (l, kind) => {
    // Almacenaje vencido: arranca en los meses que debe (no se puede pagar menos)
    setPayTarget(l); setPayKind(kind); setAmount(''); setMonths(Math.max(1, owedMonths(l, settings))); setPayError(''); setPaySuccess(null); setCardComplete(false); setSelectedPM('');
    if (user && user.email) loadPaymentMethods(user.email);
    // Montar campos de tarjeta (separados: número / vence / CVC)
    setTimeout(async () => {
      try {
        const stripeObj = stripeRef.current || await loadStripe(STRIPE_PK);
        stripeRef.current = stripeObj;
        const elements = stripeObj.elements();
        const number = elements.create('cardNumber', { style: CARD_EL_STYLE, placeholder: '1234 1234 1234 1234' });
        const expiry = elements.create('cardExpiry', { style: CARD_EL_STYLE });
        const cvc = elements.create('cardCvc', { style: CARD_EL_STYLE });
        const parts = { number: false, expiry: false, cvc: false };
        const watch = (k, el) => el.on('change', (ev) => { parts[k] = ev.complete === true; setCardComplete(parts.number && parts.expiry && parts.cvc); if (!ev.empty) setSelectedPM(''); });
        watch('number', number); watch('expiry', expiry); watch('cvc', cvc);
        if (document.getElementById('lay-card-number')) {
          number.mount('#lay-card-number'); expiry.mount('#lay-card-expiry'); cvc.mount('#lay-card-cvc');
        }
        cardRef.current = { number, expiry, cvc };
      } catch (e) { console.error(e); }
    }, 250);
  };

  const closePay = () => {
    try { if (cardRef.current) { cardRef.current.number.destroy(); cardRef.current.expiry.destroy(); cardRef.current.cvc.destroy(); } } catch (e) { /* noop */ }
    cardRef.current = null; setPayTarget(null);
  };

  const amountCents = Math.round((parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0) * 100);
  const unitsOf = (l) => (l && l.kind === 'custody' ? Math.max(1, Number(l.firearmCount) || 1) : 1);
  const storageSubtotal = (Number(settings.storageFeeCents) || 0) * months * unitsOf(payTarget);
  const storageTax = settings.storageFeeTaxable ? TAX(storageSubtotal) : 0;
  const extFee = Number(settings.extensionFeeCents) || 0;
  const extTax = settings.extensionFeeTaxable !== false ? TAX(extFee) : 0;
  const extDays = Number(settings.extensionDays) || 60;
  const totalToPay = payKind === 'storage' ? storageSubtotal + storageTax : payKind === 'extension' ? extFee + extTax : amountCents;
  const payOwed = payTarget ? owedMonths(payTarget, settings) : 0;
  const mustPayOwed = settings.storageMustPayOwed !== false && payOwed > 0;

  const handlePay = async () => {
    if (!payTarget) return;
    const usingSaved = Boolean(selectedPM);
    if (!usingSaved && !cardComplete) { setPayError('Completa número, vencimiento (MM/AA) y CVC de la tarjeta, o escoge una guardada.'); return; }
    if (payKind === 'payment') {
      if (!(amountCents > 0)) { setPayError('Escribe la cantidad a abonar'); return; }
      if (amountCents > payTarget.balanceCents) { setPayError(`El abono no puede ser mayor que el balance (${formatPrice(payTarget.balanceCents)})`); return; }
      if (amountCents < payTarget.balanceCents && amountCents < (settings.minPaymentCents || 0)) { setPayError(`El abono mínimo es ${formatPrice(settings.minPaymentCents)}`); return; }
    }
    if (payKind === 'storage' && mustPayOwed && months < payOwed) { setPayError(`Debes ${payOwed} meses de almacenaje; el pago tiene que cubrir todos los meses vencidos.`); return; }
    setProcessing(true); setPayError('');
    try {
      const body = {
        layawayId: payTarget.id, memberId: user.id || null, phone: user.phone || null, email: user.email || null,
        kind: payKind, amountCents, months, stripeCustomerId, paymentMethodId: usingSaved ? selectedPM : null, saveCard: !usingSaved && saveCard,
      };
      const r = await axios.post(`${FUNCTIONS_BASE}/createLayawayPayment`, body);
      const { clientSecret, paymentIntentId, status, requiresAction } = r.data;
      const stripeObj = stripeRef.current || await loadStripe(STRIPE_PK);
      if (!usingSaved) {
        const res = await stripeObj.confirmCardPayment(clientSecret, { payment_method: { card: cardRef.current.number } });
        if (res.error) throw new Error(res.error.message);
      } else if (requiresAction || status === 'requires_action') {
        const res = await stripeObj.confirmCardPayment(clientSecret);
        if (res.error) throw new Error(res.error.message);
      }
      const c = await axios.post(`${FUNCTIONS_BASE}/confirmLayawayPayment`, { layawayId: payTarget.id, paymentIntentId });
      if (c.data.status !== 'succeeded') throw new Error('El pago no se completó. Intenta de nuevo.');
      setPaySuccess({ total: totalToPay, layaway: c.data.layaway });
      await fetchLayaways();
    } catch (e) {
      setPayError((e.response && e.response.data && e.response.data.error) || e.message || 'No se pudo procesar el pago');
    } finally { setProcessing(false); }
  };

  // ----- Render -----
  const header = (
    <Box sx={{ bgcolor: '#1a1a1a', color: '#fff', py: 3, mb: 3 }}>
      <Container maxWidth="sm">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Mis Layaways</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>B&B Target Center · Armas separadas</Typography>
          </div>
          {user && pinVerified && <Button size="small" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} onClick={handleLogout}>Salir</Button>}
        </Stack>
      </Container>
    </Box>
  );

  if (!user || !pinVerified) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8' }}>
        {header}
        <Container maxWidth="sm">
          <Card sx={{ p: 3 }}>
            {!user && (
              <Stack spacing={2}>
                <Typography variant="h6">Entra con tu teléfono</Typography>
                <Typography variant="body2" color="text.secondary">Usa el mismo número que diste en la tienda al firmar el contrato de layaway.</Typography>
                {loginError && <Alert severity="error">{loginError}</Alert>}
                <TextField label="Teléfono" value={loginInput} onChange={(e) => setLoginInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} placeholder="787-555-1234" fullWidth />
                <Button variant="contained" size="large" onClick={handleLogin} disabled={loginLoading} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>{loginLoading ? <CircularProgress size={22} color="inherit" /> : 'Continuar'}</Button>
              </Stack>
            )}
            {user && pinStep === 'verify' && (
              <Stack spacing={2}>
                <Typography variant="h6">Hola{user.fullName ? `, ${user.fullName.split(' ')[0]}` : ''} 👋</Typography>
                <Typography variant="body2" color="text.secondary">Escribe tu PIN de 4 dígitos.</Typography>
                {pinError && <Alert severity="error">{pinError}</Alert>}
                <TextField label="PIN" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4, style: { textAlign: 'center', fontSize: 24, letterSpacing: 12 } }} value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={(e) => e.key === 'Enter' && handlePinVerify()} />
                <Button variant="contained" size="large" onClick={handlePinVerify} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>Entrar</Button>
                <Button size="small" onClick={() => { setUser(null); setPinStep(null); setPinValue(''); }}>Usar otro teléfono</Button>
                <Typography variant="caption" color="text.secondary" textAlign="center">¿Olvidaste tu PIN? Puedes restablecerlo desde la app o en /make-reservation.</Typography>
              </Stack>
            )}
            {user && pinStep === 'create' && (
              <Stack spacing={2}>
                <Typography variant="h6">Crea tu PIN</Typography>
                <Typography variant="body2" color="text.secondary">Es tu clave de 4 dígitos para entrar la próxima vez (también sirve en la app).</Typography>
                {pinError && <Alert severity="error">{pinError}</Alert>}
                <TextField label="PIN (4 dígitos)" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <TextField label="Confirmar PIN" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <Button variant="contained" size="large" onClick={handlePinCreate} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>Guardar y entrar</Button>
              </Stack>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8', pb: 6 }}>
      {header}
      <Container maxWidth="sm">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {!loading && layaways.length === 0 && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">No tienes layaways</Typography>
            <Typography variant="body2" color="text.secondary">Cuando separes un arma o dejes armas en custodia en la tienda, aparecerán aquí para que puedas pagar desde tu teléfono.</Typography>
          </Card>
        )}
        <Stack spacing={2}>
          {layaways.map((l) => {
            const st = l.overdue ? 'overdue' : l.status;
            const meta = STATUS[st] || STATUS.active;
            const pct = l.priceCents > 0 ? Math.min(100, Math.round((l.paidCents / l.priceCents) * 100)) : 0;
            const custody = l.kind === 'custody';
            const units = unitsOf(l);
            const feeMonth = (Number(settings.storageFeeCents) || 0) * units;
            const feeMonthTotal = feeMonth + (settings.storageFeeTaxable ? TAX(feeMonth) : 0);
            const owed = owedMonths(l, settings);
            const paidOff = !custody && l.status === 'paid';
            const canPay = !custody && settings.customerCanPay !== false && l.status === 'active' && l.balanceCents > 0;
            // Con balance: extensión de contrato (cláusula 8). Saldada: almacenaje (cláusula 7).
            const extLeft = (Number(settings.extensionMaxCount) || 0) === 0 || (Number(l.extensionCount) || 0) < (Number(settings.extensionMaxCount) || 0);
            const canExtend = settings.customerCanExtend !== false && (typeof l.canExtendContract === 'boolean' ? l.canExtendContract : (l.status === 'active' && l.balanceCents > 0 && extLeft));
            // Arma saldada: puede pagar almacenaje aunque no esté vencida (Tuto 2026-10-09)
            const canStorage = settings.customerCanExtend !== false && (custody ? l.status === 'active' : paidOff);
            const extTotal = (Number(settings.extensionFeeCents) || 0) + (settings.extensionFeeTaxable !== false ? TAX(Number(settings.extensionFeeCents) || 0) : 0);
            if (custody) {
              return (
                <Card key={l.id} sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <div>
                      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Custodia · {units} arma{units === 1 ? '' : 's'}</Typography>
                      <Typography variant="caption" color="text.secondary">Almacenaje {formatPrice(settings.storageFeeCents)} por arma al mes · todas en un solo pago</Typography>
                    </div>
                    <Chip size="small" color={meta.color} label={st === 'completed' ? 'Retirada' : st === 'overdue' ? 'Almacenaje vencido' : meta.label} />
                  </Stack>
                  {(l.firearms || []).map((f, i) => (
                    <Typography key={i} variant="body2" sx={{ py: 0.25 }}>{i + 1}. <b>{[f.brand, f.model].filter(Boolean).join(' ') || f.firearmType}</b>{f.caliber ? ` · ${f.caliber}` : ''}{f.serialNumber ? ` · Serie ${f.serialNumber}` : ''}</Typography>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Almacenaje pagado hasta</Typography>
                    <Typography variant="body2" fontWeight={700} color={l.overdue ? 'error.main' : l.daysLeft !== null && l.daysLeft <= 7 ? 'warning.main' : 'text.primary'}>
                      {fmtDate(l.dueDate)}{l.status === 'active' && l.daysLeft !== null ? (l.daysLeft < 0 ? ` · venció hace ${-l.daysLeft} d` : l.daysLeft === 0 ? ' · hoy' : ` · ${l.daysLeft} d`) : ''}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Mensualidad ({units} arma{units === 1 ? '' : 's'})</Typography>
                    <Typography variant="body2" fontWeight={700}>{formatPrice(feeMonthTotal)}{settings.storageFeeTaxable ? ' incl. IVU' : ''}</Typography>
                  </Stack>
                  {l.overdue && <Alert severity="warning" sx={{ mt: 1 }}>Tu almacenaje venció. Debes {owed} mes{owed === 1 ? '' : 'es'} ({formatPrice(feeMonthTotal * owed)}) y el pago tiene que cubrirlos todos. Según el contrato, pasados {settings.forfeitAfterMonths || 6} meses sin pagar la tienda puede disponer de las armas.</Alert>}
                  {canStorage && (
                    <Button fullWidth variant="contained" sx={{ mt: 2, bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }} onClick={() => openPay(l, 'storage')}>Pagar almacenaje{owed > 0 ? ` · ${owed} mes${owed === 1 ? '' : 'es'} (${formatPrice(feeMonthTotal * owed)})` : ''}</Button>
                  )}
                  {l.payments && l.payments.filter((p) => p.kind !== 'note' && !p.voided).length > 0 && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>HISTORIAL</Typography>
                      {l.payments.filter((p) => p.kind !== 'note' && !p.voided).slice().reverse().map((p) => (
                        <Stack key={p.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                          <Typography variant="body2">{KIND[p.kind] || p.kind}{p.months ? ` (${p.months} mes${p.months > 1 ? 'es' : ''})` : ''} <Typography component="span" variant="caption" color="text.secondary">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-PR') : ''}</Typography></Typography>
                          <Typography variant="body2" fontWeight={600}>{formatPrice(p.totalCents || p.amountCents)}</Typography>
                        </Stack>
                      ))}
                    </Box>
                  )}
                </Card>
              );
            }
            return (
              <Card key={l.id} sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <div>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{[l.brand, l.model].filter(Boolean).join(' ') || l.firearmType}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.firearmType}{l.caliber ? ` · ${l.caliber}` : ''}{l.serialNumber ? ` · Serie ${l.serialNumber}` : ''}</Typography>
                  </div>
                  <Chip size="small" color={meta.color} label={paidOff && l.overdue ? 'Saldada · almacenaje vencido' : meta.label} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">Balance</Typography>
                  <Typography variant="h4" color={l.balanceCents > 0 ? '#d40100' : 'success.main'}>{formatPrice(l.balanceCents)}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, my: 1 }} color={pct >= 100 ? 'success' : 'primary'} />
                <Typography variant="caption" color="text.secondary">Pagado {formatPrice(l.paidCents)} de {formatPrice(l.priceCents)}{l.priceTaxCents > 0 ? ` (precio ${formatPrice(l.basePriceCents)} + IVU ${formatPrice(l.priceTaxCents)})` : ''}</Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{paidOff ? 'Recoger antes del' : 'Fecha límite'}</Typography>
                  <Typography variant="body2" fontWeight={700} color={l.overdue ? 'error.main' : l.daysLeft !== null && l.daysLeft <= 7 ? 'warning.main' : 'text.primary'}>
                    {fmtDate(l.dueDate)}{(l.status === 'active' || paidOff) && l.daysLeft !== null ? (l.daysLeft < 0 ? ` · venció hace ${-l.daysLeft} d` : l.daysLeft === 0 ? ' · hoy' : ` · ${l.daysLeft} d`) : ''}
                  </Typography>
                </Stack>
                {l.overdue && !paidOff && <Alert severity="warning" sx={{ mt: 1 }}>Tu plazo venció sin saldar. {canExtend ? `Puedes extender el contrato ${extDays} días por ${formatPrice(extTotal)} o saldar el balance.` : 'Ya usaste la extensión que permite el contrato: salda el balance o pasa por la tienda.'}{settings.autoCancelEnabled !== false && l.dueDate ? ` Si no hay pago ni extensión antes del ${fmtDate(new Date(new Date(l.dueDate).getTime() + (Number(settings.autoCancelGraceDays) || 0) * 86400000))}, el layaway se cancela automáticamente (cláusula 16).` : ''}</Alert>}
                {paidOff && !l.overdue && <Alert severity="success" sx={{ mt: 1 }}>¡Saldada! Pasa por la tienda con tu licencia para recogerla antes del {fmtDate(l.dueDate)}. Después aplica almacenaje de {formatPrice(settings.storageFeeCents)}/mes.</Alert>}
                {paidOff && l.overdue && <Alert severity="warning" sx={{ mt: 1 }}>Tu arma está saldada pero pasó la fecha para recogerla. Debes {owed} mes{owed === 1 ? '' : 'es'} de almacenaje ({formatPrice(feeMonthTotal * owed)}) y el pago tiene que cubrirlos todos. Pasados {settings.forfeitAfterMonths || 6} meses sin pagar, la tienda puede disponer del arma.</Alert>}
                {(canPay || canExtend || canStorage) && (
                  <Stack direction="row" spacing={1} mt={2}>
                    {canPay && <Button fullWidth variant="contained" onClick={() => openPay(l, 'payment')} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>Abonar</Button>}
                    {canExtend && <Button fullWidth variant="outlined" onClick={() => openPay(l, 'extension')}>Extender {extDays} días ({formatPrice(extTotal)})</Button>}
                    {canStorage && <Button fullWidth variant={owed > 0 ? 'contained' : 'outlined'} onClick={() => openPay(l, 'storage')} sx={owed > 0 ? { bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } } : undefined}>{owed > 0 ? `Pagar almacenaje · ${owed} mes${owed === 1 ? '' : 'es'} (${formatPrice(feeMonthTotal * owed)})` : `Pagar almacenaje (${formatPrice(feeMonthTotal)}/mes)`}</Button>}
                  </Stack>
                )}
                {l.payments && l.payments.filter((p) => p.kind !== 'note' && !p.voided).length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>HISTORIAL</Typography>
                    {l.payments.filter((p) => p.kind !== 'note' && !p.voided).slice().reverse().map((p) => (
                      <Stack key={p.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography variant="body2">{KIND[p.kind] || p.kind}{p.months ? ` (${p.months} mes${p.months > 1 ? 'es' : ''})` : ''} <Typography component="span" variant="caption" color="text.secondary">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-PR') : ''}</Typography></Typography>
                        <Typography variant="body2" fontWeight={600}>{formatPrice(p.totalCents || p.amountCents)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Card>
            );
          })}
        </Stack>
        {settings.termsText && (
          <Card sx={{ p: 2, mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" onClick={() => setTermsOpen((v) => !v)} sx={{ cursor: 'pointer' }}>
              <Typography variant="subtitle1">Reglas y condiciones del layaway</Typography>
              <Typography variant="body2" color="text.secondary">{termsOpen ? 'Ocultar' : 'Ver'}</Typography>
            </Stack>
            {termsOpen && (
              <Box component="ol" sx={{ pl: 2.5, mt: 1.5, mb: 0 }}>
                {String(settings.termsText).split('\n').map((t) => t.trim()).filter(Boolean).map((t, i) => (
                  <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{t.replace(/^\d+[.)]\s*/, '')}</Typography>
                ))}
              </Box>
            )}
          </Card>
        )}
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
          Dudas: (787) 755-9060 · 369 Ave. San Claudio Suite #3, San Juan
        </Typography>
      </Container>

      <Dialog open={Boolean(payTarget)} onClose={processing ? undefined : closePay} fullWidth maxWidth="xs">
        {payTarget && !paySuccess && (
          <>
            <DialogTitle>{payTarget.kind === 'custody' ? 'Pagar almacenaje de custodia' : payKind === 'storage' ? 'Pagar almacenaje' : payKind === 'extension' ? 'Extender contrato' : 'Abonar al layaway'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">{payTarget.kind === 'custody' ? `${unitsOf(payTarget)} arma(s) · ${formatPrice((Number(settings.storageFeeCents) || 0) * unitsOf(payTarget))} por mes` : `${[payTarget.brand, payTarget.model].filter(Boolean).join(' ')} · Balance ${formatPrice(payTarget.balanceCents)}`}</Typography>
                {payError && <Alert severity="error">{payError}</Alert>}
                {['payment', 'extension'].includes(payKind) && settings.customerCanExtend !== false && settings.customerCanPay !== false && payTarget.status === 'active' && payTarget.balanceCents > 0 && payTarget.canExtendContract !== false && (
                  <ToggleButtonGroup exclusive fullWidth size="small" value={payKind} onChange={(e, v) => v && setPayKind(v)}>
                    <ToggleButton value="payment">Abonar</ToggleButton>
                    <ToggleButton value="extension">Extender contrato</ToggleButton>
                  </ToggleButtonGroup>
                )}
                {payKind === 'extension' && (
                  <Alert severity="info">Extensión de contrato (cláusula 8): <b>{extDays} días</b> adicionales por <b>{formatPrice(extFee)}</b>{extTax > 0 ? ' + IVU' : ''}. Nueva fecha límite: <strong>{fmtDate(new Date(new Date(payTarget.dueDate).getTime() + extDays * 86400000).toISOString())}</strong>. El almacenaje mensual aplica solo cuando el arma esté saldada.</Alert>
                )}
                {payKind === 'payment' ? (
                  <>
                    <TextField autoFocus label="Cantidad a abonar" value={amount} onChange={(e) => setAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} helperText={`Cualquier cantidad${settings.minPaymentCents ? ` desde ${formatPrice(settings.minPaymentCents)}` : ''} hasta ${formatPrice(payTarget.balanceCents)}`} />
                    <Stack direction="row" spacing={1}>
                      {[25, 50, 100].filter((d) => d * 100 < payTarget.balanceCents).map((d) => <Button key={d} size="small" variant="outlined" onClick={() => setAmount(String(d))}>${d}</Button>)}
                      <Button size="small" variant="outlined" onClick={() => setAmount((payTarget.balanceCents / 100).toFixed(2))}>Saldar todo</Button>
                    </Stack>
                  </>
                ) : payKind === 'storage' ? (
                  <>
                    {mustPayOwed && <Alert severity="warning">Debes <b>{payOwed} mes{payOwed === 1 ? '' : 'es'}</b> de almacenaje vencido. El pago tiene que cubrirlos todos; puedes adelantar más meses si quieres.</Alert>}
                    <TextField select label="Meses de almacenaje" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
                      {Array.from(new Set([payOwed, ...[1, 2, 3, 4, 5, 6, 12]])).filter((m) => m >= 1 && (!mustPayOwed || m >= payOwed)).sort((a, b) => a - b).map((m) => <MenuItem key={m} value={m}>{m} mes{m > 1 ? 'es' : ''}{m === payOwed && payOwed > 0 ? ' (lo que debes)' : ''} · {formatPrice((Number(settings.storageFeeCents) || 0) * m * unitsOf(payTarget))}</MenuItem>)}
                    </TextField>
                    <Alert severity="info">Cada mes cubre {settings.storageMonthDays || 30} días. {payTarget.kind === 'custody' ? 'Pagado hasta' : 'Almacenaje pagado hasta'}: <strong>{fmtDate(new Date(new Date(payTarget.dueDate).getTime() + months * (settings.storageMonthDays || 30) * 86400000).toISOString())}</strong></Alert>
                  </>
                ) : null}

                {paymentMethods.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>TARJETAS GUARDADAS</Typography>
                    {paymentMethods.map((pm) => (
                      <Card key={pm.id} variant="outlined" onClick={() => setSelectedPM(pm.id)} sx={{ p: 1.5, cursor: 'pointer', borderColor: selectedPM === pm.id ? '#d40100' : undefined, bgcolor: selectedPM === pm.id ? '#FFF5F5' : undefined }}>
                        <Typography variant="body2">{(pm.card.brand || '').toUpperCase()} •••• {pm.card.last4}</Typography>
                      </Card>
                    ))}
                  </Stack>
                )}
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>{paymentMethods.length ? 'O TARJETA NUEVA' : 'TARJETA'}</Typography>
                  <Box id="lay-card-number" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44 }} />
                  <Stack direction="row" spacing={1}>
                    <Box id="lay-card-expiry" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44, flex: 1 }} />
                    <Box id="lay-card-cvc" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44, flex: 1 }} />
                  </Stack>
                  {user.email && <FormControlLabel control={<Switch size="small" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} />} label={<Typography variant="body2">Guardar tarjeta para la próxima vez</Typography>} />}
                </Stack>

                <Divider />
                {((payKind === 'storage' && storageTax > 0) || (payKind === 'extension' && extTax > 0)) && (
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">IVU 11.5%</Typography><Typography variant="body2">{formatPrice(payKind === 'storage' ? storageTax : extTax)}</Typography></Stack>
                )}
                <Stack direction="row" justifyContent="space-between"><Typography variant="subtitle1">Total</Typography><Typography variant="h5" color="#d40100">{formatPrice(totalToPay)}</Typography></Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closePay} disabled={processing}>Cancelar</Button>
              <Button variant="contained" startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <LockIcon />} onClick={handlePay} disabled={processing || !(totalToPay > 0)} sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } }}>{processing ? 'Procesando…' : `Pagar ${formatPrice(totalToPay)}`}</Button>
            </DialogActions>
          </>
        )}
        {paySuccess && (
          <>
            <DialogTitle>¡Pago recibido!</DialogTitle>
            <DialogContent dividers>
              <Alert severity="success" sx={{ mb: 2 }}>Pagaste {formatPrice(paySuccess.total)}.</Alert>
              {paySuccess.layaway.kind !== 'custody' && <Typography variant="body2">Balance: <strong>{formatPrice(paySuccess.layaway.balanceCents)}</strong></Typography>}
              <Typography variant="body2">{paySuccess.layaway.kind === 'custody' || paySuccess.layaway.status === 'paid' ? 'Almacenaje pagado hasta' : 'Fecha límite'}: <strong>{fmtDate(paySuccess.layaway.dueDate)}</strong></Typography>
              {paySuccess.layaway.status === 'paid' && <Typography variant="body2" sx={{ mt: 1 }}>¡Saldaste tu arma! Pasa por la tienda con tu licencia para recogerla.</Typography>}
              {user.email && <Typography variant="caption" color="text.secondary" display="block" mt={1}>Te enviamos el recibo a {user.email}.</Typography>}
            </DialogContent>
            <DialogActions><Button variant="contained" onClick={closePay}>Listo</Button></DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
